import { useEffect, useRef } from 'react';

const cards = [
  {
    number: '01',
    label: '診断',
    image: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/130cb08b62b59a4991637d82632fc31c.png',
    title: 'シミュレーションで\n"結果"まで確認する',
    body: [
      { text: '見た目やスキャンだけでなく、治療シミュレーション（クリンチェック計画）を作成し、' },
      { text: '治療後の歯の動きや仕上がり', bold: true },
      { text: 'を事前に確認できます。' },
    ],
    note: '必要な枚数・期間・追加処置まで含めて判断',
    accent: 'from-stone-50 to-amber-50/60',
    border: 'border-amber-200',
    shadow: 'shadow-[0_4px_24px_rgba(217,119,6,0.10)]',
    numColor: 'text-amber-300',
    labelColor: 'text-amber-400',
    imageWide: true,
  },
  {
    number: '02',
    label: '分析',
    image: 'https://storage.readdy-site.link/project_files/29fc8b34-3c4c-432d-b46d-09fae4b65d73/76a3295f-cffc-4ffb-8b67-aab55d0b0501_-2026-06-05-155327.png?v=67a177fe661c37b7d9a2b090544d3ef3',
    imageWide: true,
    title: '見えないリスクまで\n事前に把握する',
    body: [
      { text: '歯並びだけでなく、CBCTおよびセファロ分析で骨格や顎関節、骨の厚みまで含めて分析することで、' },
      { text: '無理のない、安全性', bold: true },
      { text: 'を考えた治療計画を立てることができます。' },
    ],
    note: '無理な移動や後戻りリスクを防ぐ',
    accent: 'from-stone-50 to-teal-50/60',
    border: 'border-teal-200',
    shadow: 'shadow-[0_4px_24px_rgba(13,148,136,0.10)]',
    numColor: 'text-teal-300',
    labelColor: 'text-teal-400',
  },
  {
    number: '03',
    label: '選択',
    image: 'https://storage.readdy-site.link/project_files/29fc8b34-3c4c-432d-b46d-09fae4b65d73/dcf9dfac-20f6-4ecf-9222-46e008ced883_shinsapporo-491.jpg?v=3a79ee722e2dd89bcbb9c780e03362e8',
    imageWide: true,
    title: 'あなたに合う方法を\n比較して選べる',
    body: [
      { text: 'マウスピースだけでなく、ワイヤー矯正を含めた複数の治療法を比較し、' },
      { text: 'あなたにとって最適な選択', bold: true },
      { text: 'を整理した上で判断できます。' },
    ],
    note: '適さない場合は正直にお伝えします',
    accent: 'from-stone-50 to-rose-50/80',
    border: 'border-rose-300',
    shadow: 'shadow-[0_4px_28px_rgba(225,29,72,0.13)]',
    numColor: 'text-rose-400',
    labelColor: 'text-rose-500',
  },
];

type BodyPart = { text: string; bold?: boolean };

function CardBody({ parts }: { parts: BodyPart[] }) {
  return (
    <p className="text-gray-600 text-sm leading-relaxed">
      {parts.map((part, i) =>
        part.bold ? (
          <strong key={i} className="font-semibold text-gray-900">
            {part.text}
          </strong>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </p>
  );
}

export default function MouthpieceWhyWeCanJudge() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targets = entry.target.querySelectorAll('.fade-up, .card-stagger');
            targets.forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 90);
            });
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-stone-50/70">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* サブコピー */}
        <p
          className="text-center text-xs tracking-[0.2em] text-gray-400 mb-5 fade-up"
          style={{ fontFamily: 'Noto Serif JP, serif' }}
        >
          &ldquo;なんとなく&rdquo;ではなく、根拠をもとに判断します
        </p>

        {/* タイトル */}
        <h2
          className="text-2xl md:text-3xl lg:text-4xl font-normal text-center text-gray-900 mb-5 fade-up font-serif"
        >
          なぜ、当院は&ldquo;判断できる&rdquo;のか
        </h2>

        {/* リード文 */}
        <p className="text-center text-gray-500 text-sm md:text-base leading-relaxed mb-16 fade-up">
          診断の精度が高いほど、<br className="hidden sm:block" />
          「後悔しない選択」ができるようになります。
        </p>

        {/* カード群 — PCは3列、スマホは1列スクロール */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-14">
          {cards.map((card) => (
            <div
              key={card.number}
              className={`card-stagger rounded-2xl border ${card.border} ${card.shadow} bg-gradient-to-b ${card.accent} px-6 pt-8 pb-7 flex flex-col gap-4`}
            >
              {/* 番号 + ラベル + 画像 */}
              <div className={`flex ${card.imageWide ? 'flex-col gap-3' : 'items-start justify-between'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-0.5">
                    <span
                      className={`text-5xl font-light leading-none ${card.numColor}`}
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {card.number}
                    </span>
                    <span className={`text-xs font-medium tracking-widest ${card.labelColor}`}>
                      {card.label}
                    </span>
                  </div>
                  {!card.imageWide && (
                    <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded-lg">
                      <img
                        src={card.image}
                        alt={card.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
                {card.imageWide && (
                  <div className="w-full h-24 overflow-hidden rounded-xl">
                    <img
                      src={card.image}
                      alt={card.label}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              {/* タイトル */}
              <h3
                className="text-lg font-semibold text-gray-900 leading-snug whitespace-pre-line font-serif"
              >
                {card.title}
              </h3>

              {/* 本文 */}
              <CardBody parts={card.body} />

              {/* 補足 */}
              <p className="text-xs text-gray-400 border-t border-gray-200/70 pt-3 mt-auto leading-relaxed">
                {card.note}
              </p>
            </div>
          ))}
        </div>

        {/* スマホ: 1カードずつスクロール */}
        <div className="md:hidden flex flex-col gap-5 mb-14">
          {cards.map((card) => (
            <div
              key={card.number}
              className={`card-stagger rounded-2xl border ${card.border} ${card.shadow} bg-gradient-to-b ${card.accent} px-6 pt-8 pb-7 flex flex-col gap-4`}
            >
              <div className={`flex ${card.imageWide ? 'flex-col gap-3' : 'items-start justify-between'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-0.5">
                    <span
                      className={`text-5xl font-light leading-none ${card.numColor}`}
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {card.number}
                    </span>
                    <span className={`text-xs font-medium tracking-widest ${card.labelColor}`}>
                      {card.label}
                    </span>
                  </div>
                  {!card.imageWide && (
                    <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded-lg">
                      <img
                        src={card.image}
                        alt={card.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
                {card.imageWide && (
                  <div className="w-full h-24 overflow-hidden rounded-xl">
                    <img
                      src={card.image}
                      alt={card.label}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
              <h3
                className="text-lg font-semibold text-gray-900 leading-snug whitespace-pre-line font-serif"
              >
                {card.title}
              </h3>
              <CardBody parts={card.body} />
              <p className="text-xs text-gray-400 border-t border-gray-200/70 pt-3 mt-auto leading-relaxed">
                {card.note}
              </p>
            </div>
          ))}
        </div>

        {/* 矢印 + 接続ボックス */}
        <div className="flex flex-col items-center gap-3 fade-up">
          <div className="w-px h-10 bg-gray-300"></div>
          <div className="w-7 h-7 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-line text-xl"></i>
          </div>
          <div className="w-full max-w-xl text-center bg-white border border-gray-200 rounded-2xl px-8 py-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
            <p className="text-gray-700 text-sm md:text-base leading-loose">
              これらの診断をもとに、<br />
              あなたに合う治療法を整理したものが
            </p>
            <p
              className="text-lg md:text-xl font-semibold text-gray-900 my-3"
              style={{ fontFamily: 'Noto Serif JP, serif' }}
            >
              「矯正プラン比較レポート」です。
            </p>
            <p className="text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-4 mt-1">
              このレポートがあれば、<br />
              どの治療を選ぶべきか<strong className="font-semibold text-gray-700">迷わなくなります</strong>。
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}